import { Injectable } from '@angular/core';

export interface OrderMetadata {
  orderingType: 'dine-in' | 'off-site' | 'delivery';
  deliveryType?: 'delivery' | 'pickup';
  phone?: string;
  communicationPreference?: 'phone' | 'whatsapp' | 'viber' | 'telegram';
  paymentMethod?: string;
  deliveryAddress?: string;
  pickupLocationId?: string;
  pickupLocationName?: string;
  pickupLocationAddress?: string;
  locationId?: string;
  tableId?: string;
  tableIdentifier?: string;
  regionId?: string;
  regionName?: string;
  tableName?: string;
  orderNote?: string;
  language?: string;
  /** This device's FCM token, when the guest allowed notifications; the venue's accept/decline goes to it. */
  pushToken?: string;
}

export interface OrderPayload {
  set_paid: boolean;
  status: string;
  /**
   * Hands the venue's "new order" push to the backend instead of sending it from
   * here. An older Menu app omits this and keeps notifying the venue itself, so
   * the backend stays quiet for it and nobody gets told twice.
   */
  notify_venue?: boolean;
  customer_note?: string;
  billing?: {
    phone?: string;
    email?: string;
  };
  shipping?: {
    address_1?: string;
    phone?: string;
  };
  meta_data: Array<{
    key: string;
    value: any;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class OrderPayloadService {

  constructor() { }

  /**
   * Builds a complete order payload based on ordering type
   * @param metadata Order metadata containing all relevant information
   * @returns Complete order payload
   */
  buildOrderPayload(metadata: OrderMetadata): OrderPayload {
    const payload: OrderPayload = {
      set_paid: false,
      status: 'processing',
      // The backend decides who to notify — it is the only place that can see
      // who is actually on shift.
      notify_venue: true,
      customer_note: metadata.orderNote || '',
      meta_data: [
        {
          key: 'source',
          value: '4' // Source 4 = QR Menu (web-specific; do not change to iOS '3')
        },
        {
          // The FCM token the order-status push is sent to, as on iOS. '' when the guest has not
          // allowed notifications, and nothing is sent.
          key: 'user_token',
          value: metadata.pushToken || ''
        },
        {
          key: 'language',
          value: metadata.language || 'en'
        }
      ]
    };

    // Add payment method if selected
    if (metadata.paymentMethod) {
      payload.meta_data.push({
        key: 'payment_method',
        value: metadata.paymentMethod
      });
    }

    // Build payload based on ordering type
    if (metadata.orderingType === 'dine-in') {
      this.addDineInMetadata(payload, metadata);
    } else if (metadata.orderingType === 'delivery') {
      this.addDeliveryMetadata(payload, metadata);
    } else if (metadata.orderingType === 'off-site') {
      this.addOffSiteMetadata(payload, metadata);
    }

    return payload;
  }

  /**
   * Adds dine-in specific metadata to the order payload
   */
  private addDineInMetadata(payload: OrderPayload, metadata: OrderMetadata): void {
    payload.meta_data.push(
      {
        key: 'table_id',
        value: metadata.tableId || ''
      },
      {
        key: 'regionId',
        value: metadata.regionId || ''
      },
      {
        key: 'regionName',
        value: metadata.regionName || ''
      },
      {
        key: 'tableIdentifier',
        value: metadata.tableIdentifier || ''
      },
      {
        key: 'tableName',
        value: metadata.tableName || ''
      }
    );
  }

  /**
   * Adds delivery-specific metadata to the order payload
   * For delivery URLs with locationId (predefined delivery locations)
   */
  private addDeliveryMetadata(payload: OrderPayload, metadata: OrderMetadata): void {
    // Add billing and shipping information
    payload.billing = {
      phone: metadata.phone || '',
      email: 'email@email.com' // Default email as per spec
    };

    payload.shipping = {
      address_1: metadata.deliveryAddress || '',
      phone: metadata.phone || ''
    };

    // Add delivery-specific meta_data
    payload.meta_data.push(
      {
        key: 'outside_order_type',
        value: 'delivery' // Always delivery for this type
      },
      {
        key: 'location_id',
        value: metadata.locationId || ''
      },
      {
        key: 'communication_preference',
        value: metadata.communicationPreference || 'phone'
      }
    );
  }

  /**
   * Adds off-site specific metadata to the order payload
   * For marketing/delivery/pickup URLs (client URLs without locationId)
   */
  private addOffSiteMetadata(payload: OrderPayload, metadata: OrderMetadata): void {
    // billing/shipping are sent ONLY for delivery orders with a real address.
    // For pickup, iOS sends neither (OrderContainer.swift:576-585) — an empty
    // shipping.address_1 on pickup is what the server rejected with 400.
    if (metadata.deliveryType === 'delivery' && metadata.deliveryAddress) {
      payload.billing = {
        phone: metadata.phone || '',
        email: 'email@email.com' // Default email as per spec
      };

      payload.shipping = {
        address_1: metadata.deliveryAddress,
        phone: metadata.phone || ''
      };
    }

    // Add off-site specific meta_data (mirrors iOS OrderContainer.swift:554-572)
    payload.meta_data.push({
      key: 'outside_order_type',
      value: metadata.deliveryType || 'delivery'
    });

    if (metadata.locationId) {
      payload.meta_data.push({
        key: 'location_id',
        value: metadata.locationId
      });
    }

    if (metadata.deliveryType === 'pickup') {
      // iOS sends "{name} - {address}" and includes the key even when empty.
      payload.meta_data.push({
        key: 'pick_up_order_address',
        value: `${metadata.pickupLocationName ?? ''} - ${metadata.pickupLocationAddress ?? ''}`
      });
    } else if (metadata.deliveryType === 'delivery' && metadata.deliveryAddress) {
      payload.meta_data.push({
        key: 'delivery_address',
        value: metadata.deliveryAddress
      });
    }

    if (metadata.phone) {
      payload.meta_data.push({
        key: 'phone_number',
        value: metadata.phone
      });
    }

    payload.meta_data.push({
      key: 'communication_preference',
      value: metadata.communicationPreference || 'phone'
    });
  }

  /**
   * Validates that required fields are present for the given ordering type
   * @param metadata Order metadata to validate
   * @returns Object with isValid flag and error message if invalid
   */
  validateOrderMetadata(metadata: OrderMetadata): { isValid: boolean; error?: string } {
    if (metadata.orderingType === 'dine-in') {
      // Dine-in requires table information
      if (!metadata.tableId && !metadata.tableIdentifier) {
        return { isValid: false, error: 'Table information is required for dine-in orders' };
      }
    } else if (metadata.orderingType === 'delivery') {
      // Delivery requires phone, address, and locationId
      if (!metadata.phone) {
        return { isValid: false, error: 'Phone number is required for delivery orders' };
      }
      if (!metadata.deliveryAddress) {
        return { isValid: false, error: 'Delivery address is required for delivery orders' };
      }
      if (!metadata.locationId) {
        return { isValid: false, error: 'Location ID is required for delivery orders' };
      }
    } else if (metadata.orderingType === 'off-site') {
      // Off-site requires phone
      if (!metadata.phone) {
        return { isValid: false, error: 'Phone number is required for off-site orders' };
      }
      
      // Validate based on delivery type
      if (metadata.deliveryType === 'delivery') {
        if (!metadata.deliveryAddress) {
          return { isValid: false, error: 'Delivery address is required' };
        }
      } else if (metadata.deliveryType === 'pickup') {
        if (!metadata.pickupLocationId || metadata.pickupLocationId.trim() === '') {
          return { isValid: false, error: 'Pickup location is required' };
        }
      } else {
        // If deliveryType is not set for off-site, it's invalid
        return { isValid: false, error: 'Delivery type must be specified for off-site orders' };
      }
    }

    return { isValid: true };
  }
}
